import { LocalDate } from '@js-joda/core';

import { Address, Country, PartialAddress } from '@dreryk/address-common-api';
import {
   MedicalLeaveForGuardianUpdateCommand,
   MedicalLeavePayer,
   MedicalLeavePayerApprove,
   MedicalLeavePayerCreateOrUpdate,
   MedicalLeavePuePayer,
   MedicalLeaveUpdateCommand,
} from '@dreryk/egabinet-ezla-frontend-api';
import { InsuranceType } from '@dreryk/egabinet-ezla-published-language-api';
import { Icd10Code, Icd10Entry, Icd10Name } from '@dreryk/egabinet-icd-dictionary-published-language-api';
import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { Choice, Range } from '@dreryk/watermelon';

import { GuardianMedicalLeaveFormBean } from '../guardianMedicalLeave/guardianMedicalLeave.types';
import { MedicalLeaveFormBean } from '../medicalleave/medicalLeave.types';

import { LeaveAfterTerminationOfEmploymentRecord, MedicalLeaveFormAddress } from './common.types';
import { findEndDate, findStartDate } from './formSections/leaveDateRange/leaveDateRange.utils';

export const getTodayDate = (): LocalDate => LocalDate.now();

const hasLeaveAfterTerminationOfEmploymentForIdentificationNumber = (
   leaveAfterTerminationOfEmploymentRecord: LeaveAfterTerminationOfEmploymentRecord | undefined,
   identificationNumber: string
): boolean => {
   return Boolean(
      leaveAfterTerminationOfEmploymentRecord &&
         identificationNumber in leaveAfterTerminationOfEmploymentRecord &&
         leaveAfterTerminationOfEmploymentRecord[identificationNumber]
   );
};

export const isInsuredInPoland = (insuranceType: InsuranceType | undefined): boolean => {
   return insuranceType === 'ZUS' || insuranceType === 'OTHER_POLAND';
};

export const isInsuredInForeign = (insuranceType: InsuranceType | undefined): boolean => {
   return insuranceType === 'OTHER_FOREIGN';
};

export const mapMedicalLeavePayersToMedicalLeavePuePayers = (
   payers: MedicalLeavePayer[] | undefined
): MedicalLeavePuePayer[] => {
   if (!payers) {
      return [];
   }

   return payers.map((payer) => ({
      name: payer.name,
      identificationNumber: payer.identificationNumber!.identificationNumber!,
      identificationNumberType: payer.identificationNumber!.identificationNumberType,
   }));
};

export const mapMedicalLeavePuePayersToMedicalLeavePayerApprove = (
   payers: MedicalLeavePuePayer[] | null,
   leaveAfterTerminationOfEmploymentRecord: LeaveAfterTerminationOfEmploymentRecord
): MedicalLeavePayerApprove[] => {
   if (!payers) {
      return [];
   }

   return payers
      .filter((payer) => payer.name && payer.identificationNumber && payer.identificationNumberType)
      .map((payer) => {
         return {
            name: payer.name!,
            identificationNumber: payer.identificationNumber,
            identificationNumberType: payer.identificationNumberType,
            afterTerminationOfEmployment: hasLeaveAfterTerminationOfEmploymentForIdentificationNumber(
               leaveAfterTerminationOfEmploymentRecord,
               payer.identificationNumber
            ),
         };
      });
};

export const mapMedicalLeavePuePayersToMedicalLeavePayerCreateOrUpdate = (
   payers: MedicalLeavePuePayer[] | null,
   leaveAfterTerminationOfEmploymentRecord: LeaveAfterTerminationOfEmploymentRecord
): MedicalLeavePayerCreateOrUpdate[] => {
   if (!payers) {
      return [];
   }

   return payers.map((payer) => {
      return {
         name: Choice.withUseDefaultIfNull(payer.name!),
         identificationNumber: Choice.withUseDefaultIfNull(payer.identificationNumber),
         identificationNumberType: Choice.withUseDefaultIfNull(payer.identificationNumberType),
         afterTerminationOfEmployment: hasLeaveAfterTerminationOfEmploymentForIdentificationNumber(
            leaveAfterTerminationOfEmploymentRecord,
            payer.identificationNumber
         ),
      };
   });
};

export const findDefaultVisitDisease = (visit: Visit): Icd10Entry | null => {
   if (visit.diagnoses && visit.diagnoses.length === 1) {
      const [firstDiagnosis] = visit.diagnoses;

      return Icd10Entry.of(Icd10Code.of(firstDiagnosis.icd10Code), Icd10Name.of(firstDiagnosis.name));
   }

   return null;
};

// TODO remove when DRWEB-4260 is done
export const createNewDateRange = (startDate: LocalDate | null, endDate: LocalDate | null): Range<LocalDate> | null => {
   if (startDate && !endDate) {
      return Range.atLeast<LocalDate>(startDate);
   }
   if (!startDate && endDate) {
      return Range.atMost<LocalDate>(endDate);
   }
   if (!startDate && !endDate) {
      const today = getTodayDate();
      return Range.closed(today, today);
   }
   return Range.closed<LocalDate>(startDate!, endDate!);
};

export const mapPatientAddressToMedicalLeaveFormAddress = (address: Address): MedicalLeaveFormAddress => ({
   city: address.findTown()?.getName() ?? null,
   flat: address.findFlat()?.asString() ?? null,
   street: address.findStreet()?.asString() ?? null,
   house: address.findBuilding()?.asString() ?? null,
   postcode: address.findPostcode() ?? null,
   country: address.getCountry(),
});

export const mapMedicalLeaveFormAddressToPatientAddress = (address: MedicalLeaveFormAddress): Address => {
   return PartialAddress.builder()
      .withCountry(address.country ?? Country.DEFAULT)
      .withPostcode(address.postcode)
      .withTown(address.city)
      .withStreet(address.street)
      .withBuilding(address.house)
      .withFlat(address.flat)
      .build();
};

export const medicalLeaveDraftCommandTransformer = <
   COMMAND extends MedicalLeaveUpdateCommand | MedicalLeaveForGuardianUpdateCommand
>(
   externalId: string,
   command: COMMAND,
   variables: COMMAND extends MedicalLeaveUpdateCommand
      ? Omit<MedicalLeaveFormBean, 'diagnosis'>
      : Omit<GuardianMedicalLeaveFormBean, 'guardian' | 'relationCode' | 'birthDate'>
): void => {
   const {
      insuranceType,
      leaveDateRange,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      leaveDaysCount,
      reason,
      address,
      payers,
      leaveAfterTerminationOfEmployment,
      ...restFormValues
   } = variables;
   command.externalId = externalId;
   command.medicalLeaveDetails = {
      insuranceType: Choice.of(insuranceType),
      // TODO DRWEB-4260 use Range
      dateFrom: findStartDate(leaveDateRange) ? Choice.of(findStartDate(leaveDateRange)) : undefined,
      dateTo: findEndDate(leaveDateRange) ? Choice.of(findEndDate(leaveDateRange)) : undefined,
      reason: Choice.withUseDefaultIfNull(reason),
      address: Choice.withUseDefaultIfNull(address ? mapMedicalLeaveFormAddressToPatientAddress(address) : null),
      payers: mapMedicalLeavePuePayersToMedicalLeavePayerCreateOrUpdate(payers, leaveAfterTerminationOfEmployment),
      ...restFormValues,
   };
};
